import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { AskRequest } from '@hone-automation/common';
import { groupRequestsByDate, DateGroupKey, type DateField, type DateGroup } from '../utils/dateGrouping';

/** Minimum number of requests to show before collapsing remaining groups */
const MIN_VISIBLE_REQUESTS = 15;

export interface UseDateGroupsOptions {
  /** Requests to group (after all filters applied) */
  filteredRequests: AskRequest[];
  /** Sort order: true = oldest first, false = newest first */
  sortAscending: boolean;
  /** Date field to use for grouping */
  dateField: DateField;
}

export interface UseDateGroupsReturn {
  /** Grouped requests */
  dateGroups: DateGroup[];
  /** Set of collapsed group keys */
  collapsedGroups: Set<DateGroupKey>;
  /** Whether collapsed state has been initialized */
  hasInitialized: boolean;
  /** Toggle a group's collapsed state */
  toggleGroup: (groupKey: DateGroupKey) => void;
  /** Toggle all groups (expand all if any collapsed, collapse all if all expanded) */
  toggleAllGroups: () => void;
  /** Reset collapsed state based on current groups */
  resetCollapsedGroups: () => void;
  /** Expand a specific group */
  expandGroup: (groupKey: DateGroupKey) => void;
  /** Collapse a specific group */
  collapseGroup: (groupKey: DateGroupKey) => void;
  /** Set collapsed groups directly */
  setCollapsedGroups: React.Dispatch<React.SetStateAction<Set<DateGroupKey>>>;
  /** Flatten visible requests (excluding collapsed groups) */
  visibleRequests: AskRequest[];
  /** Find which group contains a request */
  findGroupForRequest: (requestId: string) => DateGroupKey | null;
}

/**
 * Hook for managing date-based request grouping and collapsed state
 */
export const useDateGroups = ({
  filteredRequests,
  sortAscending,
  dateField,
}: UseDateGroupsOptions): UseDateGroupsReturn => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<DateGroupKey>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);
  const hasInitializedRef = useRef(false);

  // Group requests by date
  const dateGroups = useMemo(() => {
    return groupRequestsByDate(filteredRequests, sortAscending, dateField);
  }, [filteredRequests, sortAscending, dateField]);

  // Compute initial collapsed state (show MIN_VISIBLE_REQUESTS, collapse rest)
  const computeInitialCollapsed = useCallback((groups: DateGroup[]): Set<DateGroupKey> => {
    const collapsed = new Set<DateGroupKey>();
    let visibleCount = 0;

    groups.forEach(group => {
      if (visibleCount >= MIN_VISIBLE_REQUESTS) {
        collapsed.add(group.key);
      } else {
        visibleCount += group.requests.length;
      }
    });

    return collapsed;
  }, []);

  // Initialize collapsed state on first load
  useEffect(() => {
    if (dateGroups.length > 0 && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setHasInitialized(true);
      setCollapsedGroups(computeInitialCollapsed(dateGroups));
    }
  }, [dateGroups, computeInitialCollapsed]);

  // Reset collapsed state
  const resetCollapsedGroups = useCallback(() => {
    if (hasInitializedRef.current) {
      setCollapsedGroups(computeInitialCollapsed(dateGroups));
    }
  }, [dateGroups, computeInitialCollapsed]);

  // Toggle a single group
  const toggleGroup = useCallback((groupKey: DateGroupKey) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  }, []);

  // Toggle all groups
  const toggleAllGroups = useCallback(() => {
    const allCollapsed = dateGroups.every(g => collapsedGroups.has(g.key));
    if (allCollapsed) {
      setCollapsedGroups(new Set());
    } else {
      setCollapsedGroups(new Set(dateGroups.map(g => g.key)));
    }
  }, [dateGroups, collapsedGroups]);

  // Expand a specific group
  const expandGroup = useCallback((groupKey: DateGroupKey) => {
    setCollapsedGroups(prev => {
      if (prev.has(groupKey)) {
        const newSet = new Set(prev);
        newSet.delete(groupKey);
        return newSet;
      }
      return prev;
    });
  }, []);

  // Collapse a specific group
  const collapseGroup = useCallback((groupKey: DateGroupKey) => {
    setCollapsedGroups(prev => {
      if (!prev.has(groupKey)) {
        const newSet = new Set(prev);
        newSet.add(groupKey);
        return newSet;
      }
      return prev;
    });
  }, []);

  // Flatten visible requests (excluding collapsed groups)
  const visibleRequests = useMemo(() => {
    const result: AskRequest[] = [];
    for (const group of dateGroups) {
      if (!collapsedGroups.has(group.key)) {
        result.push(...group.requests);
      }
    }
    return result;
  }, [dateGroups, collapsedGroups]);

  // Find which group contains a request
  const findGroupForRequest = useCallback(
    (requestId: string): DateGroupKey | null => {
      for (const group of dateGroups) {
        if (group.requests.some(r => r.id === requestId)) {
          return group.key;
        }
      }
      return null;
    },
    [dateGroups]
  );

  return {
    dateGroups,
    collapsedGroups,
    hasInitialized,
    toggleGroup,
    toggleAllGroups,
    resetCollapsedGroups,
    expandGroup,
    collapseGroup,
    setCollapsedGroups,
    visibleRequests,
    findGroupForRequest,
  };
};
